import { useListEnvironments } from "@/queries/environment-queries";
import { useListRunners } from "@/queries/runner-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_pb";
import {
    OnboardingStepId,
    type OrganizationOnboardingStep,
    type OrganizationOnboardingSteps,
} from "@/hooks/use-onboarding";
import { useMembers } from "@/queries/organization-queries";
import { useListAllRunnerSCMIntegrations } from "@/queries/runner-configuration-queries";

/**
 * Hook for computing the initial onboarding state for a user in an organization
 */
export function useOrganizationOnboardingSteps(): OrganizationOnboardingSteps {
    const { data: user } = useAuthenticatedUser();
    const { data: remoteRunners, isLoading: isLoadingRemoteRunners } = useListRunners({
        kind: RunnerKind.REMOTE,
    });
    const { data: localRunners, isPending: isPendingLocalRunners } = useListRunners({
        kind: RunnerKind.LOCAL,
        creatorId: user?.id,
    });

    const { data: scmIntegration, isLoading: isLoadingSCMIntegrations } = useListAllRunnerSCMIntegrations(
        remoteRunners?.runners?.map((r) => r.runnerId) || [],
    );

    const { data: environments, isLoading: isLoadingEnvironments } = useListEnvironments();
    const { data: members, isLoading: isLoadingMembers } = useMembers();

    const hasUser = !!user?.id;
    const isLoading =
        !hasUser ||
        isLoadingRemoteRunners ||
        isPendingLocalRunners ||
        isLoadingEnvironments ||
        isLoadingMembers ||
        isLoadingSCMIntegrations;

    const hasLocalRunner = (localRunners?.runners?.length ?? 0) > 0;
    const hasAtLeastOneRemoteRunner = (remoteRunners?.runners?.length ?? 0) > 0;
    const hasAtLeastOneEnvironment = (environments?.environments?.length ?? 0) > 0;
    const hasAtLeastOneOtherMember = (members?.members?.length ?? 0) > 1;
    const hasConfiguredSCMForRemoteRunner = (scmIntegration?.length ?? 0) > 0;

    let hasCompletedHowGitpodWorks = false;
    let hasCompletedSetUpARunner = false;
    let hasCompletedAutomateYourDevEnvironment = false;
    let hasCompletedShare = false;

    if (!isLoading) {
        if (hasAtLeastOneEnvironment) {
            hasCompletedHowGitpodWorks = true;
            hasCompletedSetUpARunner = true;
            hasCompletedAutomateYourDevEnvironment = true;
        } else if (hasConfiguredSCMForRemoteRunner) {
            hasCompletedHowGitpodWorks = true;
            hasCompletedSetUpARunner = true;
        } else if (hasLocalRunner) {
            hasCompletedHowGitpodWorks = true;
            hasCompletedSetUpARunner = true;
        } else if (hasAtLeastOneRemoteRunner) {
            hasCompletedHowGitpodWorks = true;
        } else {
            // Nothing is completed
        }

        // We only want to show step 4 as completed if step 3 is also completed.
        if (hasAtLeastOneOtherMember && hasCompletedAutomateYourDevEnvironment) {
            hasCompletedShare = true;
        }
    }

    const steps = mkSteps({
        [OnboardingStepId.HowGitpodWorks]: hasCompletedHowGitpodWorks,
        [OnboardingStepId.SetUpARunner]: hasCompletedSetUpARunner,
        [OnboardingStepId.AutomateYourDevEnvironment]: hasCompletedAutomateYourDevEnvironment,
        [OnboardingStepId.ShareGoldenPathWithYourTeam]: hasCompletedShare,
    });

    // The first non-completed step.
    let defaultActive: OnboardingStepId = OnboardingStepId.HowGitpodWorks;
    for (const step of steps) {
        if (!step.completed) {
            defaultActive = step.id;
            break;
        }
        defaultActive = step.id;
    }

    return {
        isLoading,
        steps,
        defaultActive,
    };
}

export function mkSteps(completed: Record<OnboardingStepId, boolean>): OrganizationOnboardingStep[] {
    return [
        {
            id: OnboardingStepId.HowGitpodWorks,
            title: "How Gitpod works",
            completed: completed[OnboardingStepId.HowGitpodWorks] ?? false,
        },
        {
            id: OnboardingStepId.SetUpARunner,
            title: "Set up a runner",
            completed: completed[OnboardingStepId.SetUpARunner] ?? false,
        },
        {
            id: OnboardingStepId.AutomateYourDevEnvironment,
            title: "Automate your dev environment",
            completed: completed[OnboardingStepId.AutomateYourDevEnvironment] ?? false,
        },
        {
            id: OnboardingStepId.ShareGoldenPathWithYourTeam,
            title: "Share with your team",
            completed: completed[OnboardingStepId.ShareGoldenPathWithYourTeam] ?? false,
        },
    ];
}
